import {
  Alert,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Text,
} from "@chakra-ui/react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import {
  useEditor,
  useFieldErrorTranslation,
} from "../../../../../common/editing/save-coordinator";
import { ThemedDateTimePicker } from "../../../../../common/input/DateTimeSelectors/ThemedDateTimePicker";
import { SUGGESTED_BID_DEADLINE_DAYS, suggestedBidDeadlineIso } from "../utils/suggestedDeadline";

interface BidSubmissionDeadlineFieldProps {
  eventId: string;
  currentDeadline: string | null;
  // null = legacy/confirmed, false = an unconfirmed suggestion, true = confirmed by the buyer.
  currentConfirmed: boolean | null;
  // The statutory minimum tender days for the event's FOA part (null when none applies).
  minDays: number | null;
  hideLabel?: boolean;
}

const FIELD_PATH = "eventData.bidSubmissionDeadline";
const END_OF_DAY = { hours: 23, minutes: 59 };

export const BidSubmissionDeadlineField = ({
  eventId,
  currentDeadline,
  currentConfirmed,
  minDays,
  hideLabel = false,
}: BidSubmissionDeadlineFieldProps) => {
  const { t } = useTranslation();
  const { updateField, fieldStatus, fieldError } = useEditor<
    SourcingEventV2Dto,
    SourcingEventV2PatchDto
  >(eventId);
  const translateFieldError = useFieldErrorTranslation();

  const error = fieldError(FIELD_PATH);
  const status = fieldStatus(FIELD_PATH);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // A null deadline only exists on a draft (the presence readiness gate guarantees published
  // events have one), so the first time the planner opens with no deadline we seed a compliant
  // suggestion — measured now, not at creation — left explicitly unconfirmed so it can't satisfy
  // publish readiness untouched. The ref keeps it to one write per mount.
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (prefilledRef.current) return;
    if (currentDeadline !== null) return;
    if (status === "saving") return;
    prefilledRef.current = true;
    updateField({
      eventData: {
        bidSubmissionDeadline: suggestedBidDeadlineIso(),
        bidSubmissionDeadlineConfirmed: false,
      },
    });
  }, [currentDeadline, status, updateField]);

  // Setting a date is itself a confirmation; clearing it leaves confirmation untouched — it is
  // meaningless without a deadline, and writing false would clobber a legacy event's null.
  const handleChange = (iso?: string) => {
    updateField({
      eventData:
        iso != null
          ? { bidSubmissionDeadline: iso, bidSubmissionDeadlineConfirmed: true }
          : { bidSubmissionDeadline: null },
    });
  };

  const handleConfirm = () => {
    updateField({ eventData: { bidSubmissionDeadlineConfirmed: true } });
  };

  const showBanner = currentConfirmed === false && currentDeadline !== null && !error;
  // A suggestion decays below the statutory minimum if the draft sits for days; don't let the buyer
  // "confirm" a too-soon date into a server-side rejection — they must pick a later one first.
  const belowMinimum =
    minDays != null &&
    currentDeadline !== null &&
    moment(currentDeadline).isBefore(moment().add(minDays, "days"));

  return (
    <FormControl isInvalid={Boolean(error)} isDisabled={status === "saving"}>
      {!hideLabel && <FormLabel htmlFor="bidSubmissionDeadline">{t("Bid deadline")}</FormLabel>}
      <ThemedDateTimePicker
        id="bidSubmissionDeadline"
        ariaLabel={hideLabel ? (t("Bid deadline") ?? undefined) : undefined}
        date={currentDeadline}
        onChange={handleChange}
        defaultTime={END_OF_DAY}
      />
      <Box mt="1">
        {error ? (
          <FormErrorMessage mt="0">{translateFieldError(error)}</FormErrorMessage>
        ) : showBanner ? (
          <Alert status="info" borderRadius="md" fontSize="sm" alignItems="flex-start" py="2">
            <Box>
              <Text>
                {t("We've suggested a bid deadline {{days}} days out. Confirm it or set your own.", {
                  days: SUGGESTED_BID_DEADLINE_DAYS,
                })}
                {minDays != null && ` ${t("The minimum is {{minDays}} days.", { minDays })}`}
              </Text>
              <Button
                mt="2"
                size="sm"
                colorScheme="teal"
                isDisabled={belowMinimum}
                onClick={handleConfirm}
              >
                {t("Confirm deadline")}
              </Button>
              {belowMinimum && (
                <Text mt="1" color="smError">
                  {t("Pick a later date before confirming.")}
                </Text>
              )}
            </Box>
          </Alert>
        ) : (
          <FormHelperText mt="0">
            {t("Tender deadline timezone: {{timezone}}", { timezone })}
          </FormHelperText>
        )}
      </Box>
    </FormControl>
  );
};
